/**
 * Module dependencies.
 */

import { GetServerSideProps } from 'next';
import { TourData, TourSlug } from 'src/types/tour';
import { absoluteUrlResolver } from 'src/core/utils/url-resolver';
import { detectMobile } from 'src/core/utils/devices';
import { media } from 'src/styles/media';
import { switchProp } from 'styled-tools';
import { tours } from 'src/core/config/tours';
import { toursAssets } from 'src/core/content-config/tour';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavbarTheme } from 'src/context/navbar-theme/context';
import { useRouter } from 'next/router';
import AuthorsNotesSection from 'src/components/sections/episode/authors-notes-section';
import BestOfSection from 'src/components/sections/episode/best-of-section';
import BookSection from 'src/components/sections/episode/book-section';
import Footer from 'src/components/footer';
import GuestSection from 'src/components/sections/episode/guest-section';
import Metatags from 'src/components/core/metatags';
import NextTourSection from 'src/components/sections/episode/next-tour-section';
import SectionFootnote from 'src/components/sections/section-footnote';
import TipsSection from 'src/components/sections/episode/tips-section';
import TriggerNavbarTheme, { useInitialNavbarTheme } from 'src/components/navbar/trigger-navbar-theme';

import VideoSection from 'src/components/sections/episode/video-section';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import styled, { css } from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';

/**
 * `Props` type.
 */

type Props = {
  slug: TourSlug;
  translations: TourData;
};

/**
 * `ColorTheme` type.
 */

type ColorTheme = 'yellow' | 'white';

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  ::-webkit-scrollbar {
    ${media.min.md`
      display: none;
    `}
  }

  ${media.min.md`
    display: flex;
    flex-wrap: nowrap;
    height: 100vh;
    overflow: auto;
    position: relative;
    scrollbar-width: none;
    -ms-overflow-style: none;
  `}
`;

/**
 * `Progress` styled component.
 */

const Progress = styled.progress<{ color: ColorTheme }>`
  appearance: none;
  border: none;
  border-radius: 0;
  bottom: 0;
  height: 0.625rem;
  position: fixed;
  width: 100%;

  ::-webkit-progress-bar {
    background-color: var(--color-black-opacity-40);
    height: 1.25rem;
  }

  ::-webkit-progress-value {
    transition: background-color var(--transition-fast);

    ${switchProp('color', {
      white: css`
        background-color: var(--color-white);
      `,
      yellow: css`
        background-color: var(--color-yellow);
      `
    })}
  }

  ::-moz-progress-bar {
    transition: background-color var(--transition-fast);

    ${switchProp('color', {
      white: css`
        background-color: var(--color-white);
      `,
      yellow: css`
        background-color: var(--color-yellow);
      `
    })}
  }
`;

/**
 * Get translations.
 */

async function getTranslations<P>(slug: string, locale: string) {
  const url = absoluteUrlResolver(`locales/${locale}/tours/${slug}.json`);
  const response = await fetch(url);
  const data = await response.json();

  return data as P;
}

/**
 * `TourDetails` page.
 */

const TourDetails = ({ slug, translations }: Props) => {
  const isMobile = useBreakpoint('md', 'max');
  const router = useRouter();
  const [isMobileDevice, setIsMobileDevice] = useState<boolean>();
  const [scrollLeft, setScrollLeft] = useState(0);
  const { setTheme } = useNavbarTheme();
  const scrollRef = useRef<HTMLDivElement>(null);
  const videoSectionWrapperRef = useRef<HTMLDivElement>(null);
  const nextTourSectionRef = useRef<HTMLDivElement>(null);
  const options = { threshold: 0.01 };
  const [bestOfSectionRef, bestOfSectionInView] = useInView(options);
  const [bookSectionRef, bookSectionInView] = useInView(options);
  const [authorsNotesSectionRef, authorsNotesSectionInView] = useInView(options);

  const [tipsSectionRef, tipsSectionInView] = useInView(options);
  const [guestSectionRef, guestSectionInView] = useInView(options);
  const [videoSectionRef, videoSectionInView] = useInView({
    threshold: 0.9
  });

  const onScroll = () => {
    if (!scrollRef.current) {
      return;
    }

    const scrolled = scrollRef.current.scrollLeft / (scrollRef.current.scrollWidth - window.innerWidth);

    setScrollLeft(scrolled);
  };

  const tourData: TourData = merge({}, translations, toursAssets[slug]);
  const footnoteDescription = useMemo(
    () =>
      (guestSectionInView && tourData?.sections?.guest.author.fullName) ||
      (bookSectionInView && tourData?.sections?.book.footNote) ||
      (authorsNotesSectionInView && tourData?.sections?.authorsNotes.footNote) ||
      (tipsSectionInView && tourData?.sections?.tips.footNote) ||
      (bestOfSectionInView && tourData?.sections.bestOf.title) ||
      undefined,
    [
      authorsNotesSectionInView,
      bestOfSectionInView,
      bookSectionInView,
      guestSectionInView,
      tipsSectionInView,
      tourData?.sections?.authorsNotes.footNote,
      tourData?.sections.bestOf.title,
      tourData?.sections.book.footNote,
      tourData?.sections?.guest.author.fullName,
      tourData?.sections.tips.footNote
    ]
  );

  const footerTheme = useMemo(() => (bookSectionInView ? 'light' : 'dark'), [bookSectionInView]);

  const progressColor = useMemo(
    () => (footnoteDescription === tourData?.sections?.tips.footNote ? 'white' : 'yellow'),
    [footnoteDescription, tourData?.sections?.tips.footNote]
  );

  useEffect(() => {
    function handleRouteChange() {
      setScrollLeft(0);
      setTheme('dark');
    }

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => router.events.off('routeChangeComplete', handleRouteChange);
  }, [router.events, setTheme]);

  useEffect(() => {
    setIsMobileDevice(detectMobile());
  }, []);

  useEffect(() => {
    function handleScroll(event: WheelEvent) {
      event.preventDefault();

      function updateScrolledDistance() {
        if (!scrollRef.current) {
          return;
        }

        scrollRef.current.scrollLeft += event.deltaY;
        scrollRef.current.scrollLeft += event.deltaX;
      }

      function handleGoToNextTour() {
        if (!nextTourSectionRef?.current) {
          return;
        }

        const gap = (nextTourSectionRef.current?.firstElementChild?.clientWidth || 0) / 2;

        const endX = nextTourSectionRef.current.offsetLeft + gap;

        if (scrollRef.current && scrollRef.current.scrollLeft + 1 >= endX) {
          router.push(tourData.sections.nextTour.slug);
        }
      }

      if (tourData.sections.nextTour.slug) {
        handleGoToNextTour();
      }

      updateScrolledDistance();
    }

    if (isMobileDevice === false && !isMobile && scrollRef.current) {
      scrollRef.current.addEventListener('wheel', handleScroll);
    }
  }, [isMobile, isMobileDevice, router, tourData?.sections?.nextTour.slug, videoSectionInView]);

  useEffect(() => {
    if (guestSectionInView && isMobile) {
      setTheme('dark');
    }
  }, [guestSectionInView, isMobile, setTheme]);

  useInitialNavbarTheme('dark');

  return (
    <>
      <Metatags pageTitle={tourData?.title} title={tourData?.title} />

      <Wrapper key={slug} onScroll={onScroll} ref={scrollRef}>
        <TriggerNavbarTheme colorTheme={'dark'} horizontalNavigation={!isMobile} />

        <GuestSection
          {...tourData?.sections?.guest}
          ref={guestSectionRef}
          subTitle={tourData?.subTitle}
          title={tourData?.title}
        />

        {!isMobile && <TriggerNavbarTheme colorTheme={'dark'} horizontalNavigation />}

        <TriggerNavbarTheme colorTheme={'light'} horizontalNavigation={!isMobile} />

        <div ref={videoSectionWrapperRef}>
          <VideoSection {...tourData?.sections?.video} isInView={videoSectionInView} ref={videoSectionRef} />
        </div>

        {!isMobile && <TriggerNavbarTheme colorTheme={'light'} horizontalNavigation />}

        <BookSection {...tourData?.sections?.book} ref={bookSectionRef} />

        {!isMobile && <TriggerNavbarTheme colorTheme={'light'} horizontalNavigation />}

        <TriggerNavbarTheme colorTheme={'dark'} horizontalNavigation={!isMobile} />

        <AuthorsNotesSection
          {...tourData?.sections?.authorsNotes}
          author={tourData?.sections?.guest?.author.fullName}
          ref={authorsNotesSectionRef}
        />

        {!isMobile && <TriggerNavbarTheme colorTheme={'dark'} horizontalNavigation />}

        <TipsSection {...tourData?.sections?.tips} ref={tipsSectionRef} />

        {!isMobile && <TriggerNavbarTheme colorTheme={'dark'} horizontalNavigation />}

        <BestOfSection data={tourData?.sections.bestOf} ref={bestOfSectionRef} slug={slug} />

        {!isMobile && <TriggerNavbarTheme colorTheme={'dark'} horizontalNavigation />}

        <TriggerNavbarTheme colorTheme={'light'} horizontalNavigation={!isMobile} />

        <NextTourSection ref={nextTourSectionRef} {...tourData?.sections?.nextTour} />

        {!isMobile && (
          <SectionFootnote
            description={footnoteDescription}
            isScrolled={scrollLeft > 0}
            label={tourData?.title}
            theme={footerTheme}
          />
        )}

        {!isMobile && <Progress color={progressColor} value={scrollLeft} />}
      </Wrapper>

      {isMobile && <Footer />}
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export const getServerSideProps: GetServerSideProps = async ctx => {
  const slug = ctx?.query?.slug as string;

  if (!find(tours, { slug })) {
    return {
      notFound: true
    };
  }

  const translations = await getTranslations<TourData>(slug, ctx?.locale as string);

  if (isEmpty(translations)) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      slug,
      translations
    }
  };
};

/**
 * Export `TourDetails` page.
 */

export default TourDetails;
